import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Leaf, Phone, LockKey, ShieldCheck, ArrowRight, KeyReturn, CheckCircle } from "@phosphor-icons/react";
import { custApi, formatErr, CUST_TOKEN_KEY, CUST_USER_KEY } from "../../lib/customerApi";
import { useCustAuth } from "../../lib/customerAuth";

// Iter 138 — MeLogin flow:
//   default   → phone → PIN (everyday login)
//   otp mode  → phone → OTP  (for accounts without a PIN yet OR PIN reset)
//
// The user can toggle to OTP mode via "Forgot passcode?" link. When
// PIN login fails with `no_pin_set` (older account), we auto-fall back
// to OTP.
export default function MeLogin() {
  const { customer, loginPin, loginOtp } = useCustAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("pin");   // "pin" | "otp" | "forgot"
  const [step, setStep] = useState(1);       // 1 = phone, 2 = code, 3 = set-pin
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [loading, setLoading] = useState(false);
  // Iter 138 — When an OTP login succeeds but the customer has no PIN
  // yet, we route them to a mandatory "Set passcode" step BEFORE the
  // main app. `newPin`/`confirmPin` are the two entry fields.
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  if (customer) return <Navigate to="/me" replace />;

  const sendOtp = async () => {
    setLoading(true);
    try {
      const r = await custApi.post("/public/customer/login/otp", { phone: phone.trim() });
      setDevOtp(r.data.dev_otp || "");
      setStep(2);
      toast.success(r.data.dev_otp ? `Demo OTP: ${r.data.dev_otp}` : "OTP sent");
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };

  const submitPhone = async (e) => {
    e.preventDefault();
    if (!/^\d{6,15}$/.test(phone.trim())) { toast.error("Enter a valid phone"); return; }
    if (mode === "otp") {
      await sendOtp();
    } else {
      setStep(2);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "pin") {
        if (pin.length !== 6) throw new Error("Enter 6-digit passcode");
        await loginPin(phone.trim(), pin);
        toast.success("Welcome back");
        nav("/me");
      } else {
        if (otp.length !== 6) throw new Error("Enter 6-digit OTP");
        // Returns full response: {token, has_pin, customer}
        const data = await loginOtp(phone.trim(), otp.trim());
        if (!data.has_pin) {
          // Existing customer without a PIN — force them to create one
          // BEFORE entering the app. Their JWT is already stored (they
          // ARE logged in) so the /me/pin/set endpoint will accept.
          toast.success("Verified — please create your 6-digit passcode");
          setStep(3);
          return;
        }
        toast.success("Welcome back");
        nav("/me");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Older account → auto-switch to OTP mode.
      if (detail && typeof detail === "object" && detail.code === "no_pin_set") {
        toast.info(detail.message || "Please login with OTP once and set your passcode");
        setMode("otp");
        setPin("");
        await sendOtp();
      } else {
        toast.error(formatErr(e));
      }
    }
    setLoading(false);
  };

  // Iter 138 — Mandatory PIN setup after first-time OTP login.
  const savePin = async (e) => {
    e.preventDefault();
    if (newPin.length !== 6) { toast.error("Passcode must be exactly 6 digits"); return; }
    if (newPin !== confirmPin) { toast.error("Both passcodes must match"); return; }
    if (/^(\d)\1{5}$/.test(newPin) || newPin === "123456" || newPin === "654321") {
      toast.error("Please pick a less predictable passcode"); return;
    }
    setLoading(true);
    try {
      await custApi.post("/me/pin/set", { new_pin: newPin });
      toast.success("Passcode saved — use it to log in next time");
      nav("/me");
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };

  const skipPinSet = () => { /* removed — passcode is now mandatory */ };

  const switchToOtp = async () => {
    // The button says "Log in with OTP" so behave literally: send an
    // OTP challenge and switch to the simple OTP step. If the customer
    // later remembers their passcode they can change PIN from settings;
    // if they truly want a full reset they can tap the small link below.
    setMode("otp");
    setPin("");
    setOtp("");
    setNewPin("");
    setConfirmPin("");
    setDevOtp("");
    await sendOtp();
  };

  const startPasscodeReset = async () => {
    // Full "reset my passcode" flow — OTP + new PIN in one form.
    setMode("forgot");
    setPin("");
    setOtp("");
    setNewPin("");
    setConfirmPin("");
    setDevOtp("");
    setLoading(true);
    try {
      const r = await custApi.post("/public/customer/pin/forgot", { phone: phone.trim() });
      setDevOtp(r.data.dev_otp || "");
      setEmailHint(r.data.email_masked || "");
      toast.success(
        r.data.dev_otp
          ? `Demo OTP: ${r.data.dev_otp}`
          : r.data.email_masked
            ? `Reset code sent to ${r.data.email_masked}`
            : "Reset code sent to your registered contact",
      );
      setStep(2);
    } catch (e) {
      toast.error(formatErr(e));
      setMode("pin");
    }
    setLoading(false);
  };

  // Iter 138 — Forgot-passcode submission. Uses the one-shot
  // /pin/reset endpoint (phone + otp + new_pin → pin_hash updated
  // server-side). After success we bounce the user back to the PIN
  // login screen with the phone pre-filled.
  const submitForgotReset = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error("Enter 6-digit reset code"); return; }
    if (newPin.length !== 6) { toast.error("Passcode must be 6 digits"); return; }
    if (newPin !== confirmPin) { toast.error("Both passcodes must match"); return; }
    if (/^(\d)\1{5}$/.test(newPin) || newPin === "123456" || newPin === "654321") {
      toast.error("Please pick a less predictable passcode"); return;
    }
    setLoading(true);
    try {
      await custApi.post("/public/customer/login/pin/reset", {
        phone: phone.trim(), otp, new_pin: newPin,
      });
      toast.success("Passcode reset — please log in with your new passcode");
      // Reset back to PIN login with phone pre-filled.
      setMode("pin");
      setStep(2);
      setOtp(""); setNewPin(""); setConfirmPin("");
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };

  return (
    <div className="min-h-[100dvh] bg-[#F9FAF9] font-body text-[#111827] flex flex-col" data-testid="me-login">
      <div className="mx-auto w-full max-w-[460px] min-h-[100dvh] bg-[#F9FAF9] shadow-[0_0_60px_rgba(15,31,12,0.08)] relative flex flex-col overflow-x-hidden">
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-[#DC2626] via-[#B91C1C] to-[#7F1D1D] text-white pt-10 pb-24 px-5 overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/15 blur-2xl"/>
          <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-black/20 blur-2xl"/>
          <div className="relative flex items-center gap-3 mb-8">
            <img src="/brand/rescigo-mark-128.png" alt="Rescigo"
                  className="w-11 h-11 rounded-xl object-contain bg-white/95 ring-1 ring-white/30 p-1 shadow-lg"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/80 font-bold">Rescigo</div>
              <div className="font-display font-bold text-lg leading-tight">Health &amp; Waste, Handled Right</div>
            </div>
          </div>
          <h1 className="relative font-display font-black text-3xl leading-[1.1] tracking-tight max-w-[280px]">
            We care for health.<br/>
            <span className="text-[#FECACA]">You focus on life.</span>
          </h1>
          <p className="relative mt-3 text-[13px] text-white/85 max-w-[280px] leading-relaxed">
            Biomedical waste pickup, home nursing and local services — all in one place.
          </p>
        </div>

        {/* Auth card — pulled up over the hero */}
        <div className="flex-1 px-5 pb-8 -mt-16 relative z-10">
          <div className="w-full">
            <div className="bg-white rounded-3xl border border-green-900/5 p-6 shadow-[0_20px_60px_rgba(15,31,12,0.10)]">
              <div className="text-[10px] uppercase tracking-[0.20em] text-[#DC2626] font-bold mb-2">
              {step === 1 ? "Step 1 · Your phone"
                          : step === 3 ? "Step 3 · Create your passcode"
                          : (mode === "pin"    ? "Step 2 · Enter passcode"
                              : mode === "forgot" ? "Step 2 · Reset passcode"
                                                   : "Step 2 · Verify OTP")}
            </div>

            {step === 2 && mode === "forgot" ? (
              // Iter 138 — Forgot passcode: one-shot form (email OTP + new PIN).
              <form onSubmit={submitForgotReset} className="space-y-4" data-testid="me-forgot-form">
                <div className="text-sm text-[#5C5A55]">
                  Resetting passcode for <span className="font-mono font-medium text-[#1A1A1A]">{phone}</span>
                  <button type="button" onClick={() => { setMode("pin"); setStep(1); }}
                          className="ml-2 text-[#DC2626] text-xs underline">change</button>
                </div>
                {devOtp ? (
                  <div className="p-3 rounded-md bg-[#F9E4B7] border border-[#E5C978] text-center">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A5A11] font-semibold">
                      Demo mode · your reset code is
                    </div>
                    <div className="mt-1 font-mono text-3xl tracking-[0.4em] text-[#7A5A11] font-bold">{devOtp}</div>
                  </div>
                ) : emailHint ? (
                  <div className="text-[11px] text-[#DC2626] bg-[#EFF3EE] border border-[#FEE2E2] rounded p-2 text-center">
                    Reset code emailed to <b>{emailHint}</b>
                  </div>
                ) : null}
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                    Reset code (from email)
                  </label>
                  <input data-testid="me-forgot-otp"
                         value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                         inputMode="numeric" maxLength={6} autoFocus placeholder="••••••"
                         className="w-full h-16 px-4 border-2 border-[#DC2626] rounded-md font-mono text-3xl tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40"/>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                    New passcode
                  </label>
                  <input data-testid="me-forgot-new"
                         type="password" inputMode="numeric" maxLength={6}
                         value={newPin}
                         onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                         placeholder="••••••"
                         className="w-full h-14 px-4 border border-[#E5E2D9] rounded-md font-mono text-2xl tracking-[0.4em] text-center focus:outline-none focus:border-[#DC2626]"/>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                    Confirm new passcode
                  </label>
                  <input data-testid="me-forgot-confirm"
                         type="password" inputMode="numeric" maxLength={6}
                         value={confirmPin}
                         onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                         placeholder="••••••"
                         className="w-full h-14 px-4 border border-[#E5E2D9] rounded-md font-mono text-2xl tracking-[0.4em] text-center focus:outline-none focus:border-[#DC2626]"/>
                  {newPin && confirmPin && newPin !== confirmPin && (
                    <div className="text-[11px] text-[#C25D38] mt-1">Passcodes don&apos;t match yet</div>
                  )}
                </div>
                <button data-testid="me-forgot-submit"
                        disabled={loading || otp.length !== 6 || newPin.length !== 6 || newPin !== confirmPin}
                        type="submit"
                        className="w-full h-12 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-medium">
                  {loading ? "Resetting…" : "Save new passcode"}
                </button>
                <button type="button" onClick={() => { setMode("pin"); setStep(2); }}
                        className="w-full text-xs text-[#DC2626] underline">
                  I remember my passcode — go back
                </button>
              </form>
            ) : step === 3 ? (
              // Iter 138 — Mandatory PIN setup for existing (PIN-less)
              // accounts after their first OTP login.
              <form onSubmit={savePin} className="space-y-4" data-testid="me-set-pin-form">
                <div className="rounded-md border border-[#FEE2E2] bg-[#EFF3EE] p-3 flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" color="#DC2626" className="shrink-0 mt-0.5"/>
                  <div>
                    <div className="text-[13px] font-medium text-[#7F1D1D]">Phone verified</div>
                    <div className="text-[11px] text-[#5C5A55] mt-0.5">
                      Set a 6-digit passcode so you can log in without OTP next time.
                      You can reset it anytime via OTP if you forget.
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                    New passcode
                  </label>
                  <input
                    data-testid="me-new-pin"
                    type="password" inputMode="numeric" maxLength={6} autoFocus
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="••••••"
                    className="w-full h-16 px-4 border-2 border-[#DC2626] rounded-md font-mono text-3xl tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                    Confirm passcode
                  </label>
                  <input
                    data-testid="me-confirm-pin"
                    type="password" inputMode="numeric" maxLength={6}
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="••••••"
                    className="w-full h-14 px-4 border border-[#E5E2D9] rounded-md font-mono text-2xl tracking-[0.4em] text-center focus:outline-none focus:border-[#DC2626]"
                  />
                  {newPin && confirmPin && newPin !== confirmPin && (
                    <div className="text-[11px] text-[#C25D38] mt-1">Passcodes don&apos;t match yet</div>
                  )}
                </div>
                <button
                  data-testid="me-save-pin"
                  disabled={loading || newPin.length !== 6 || newPin !== confirmPin}
                  type="submit"
                  className="w-full h-12 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2"
                >
                  {loading ? "Saving…" : "Save passcode & continue"} <ArrowRight size={14} weight="bold"/>
                </button>
                <div className="text-[10px] text-[#8A8780] text-center">
                  A passcode is required to continue. It can be reset anytime with an OTP.
                </div>
              </form>
            ) : step === 1 ? (
              <form onSubmit={submitPhone} className="space-y-4" data-testid="me-login-phone-form">
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]" />
                  <input
                    data-testid="me-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="e.g. 9123456789"
                    autoFocus
                    inputMode="numeric"
                    className="w-full h-12 pl-10 pr-3 border border-[#E5E2D9] rounded-md text-[15px] font-mono"
                  />
                </div>
                <button
                  data-testid="me-continue"
                  disabled={loading}
                  type="submit"
                  className="w-full h-12 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2"
                >
                  {loading ? "Please wait…" : "Continue"} <ArrowRight size={14} weight="bold" />
                </button>
                <div className="text-xs text-[#8A8780] text-center leading-relaxed">
                  {mode === "pin"
                    ? "We'll ask for your 6-digit passcode next."
                    : "We'll send a 6-digit OTP to your phone."}
                </div>
              </form>
            ) : mode === "pin" ? (
              <form onSubmit={submitCode} className="space-y-4" data-testid="me-login-pin-form">
                <div className="text-sm text-[#5C5A55]">
                  Logging in as <span className="font-mono font-medium text-[#1A1A1A]">{phone}</span>
                  <button type="button" onClick={() => setStep(1)} className="ml-2 text-[#DC2626] text-xs underline">change</button>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                    Passcode
                  </label>
                  <div className="relative">
                    <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
                    <input
                      data-testid="me-pin"
                      type="password"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="••••••"
                      autoFocus
                      inputMode="numeric"
                      maxLength={6}
                      className="w-full h-16 pl-10 pr-3 border-2 border-[#DC2626] rounded-md text-center text-3xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40"
                    />
                  </div>
                </div>
                <button
                  data-testid="me-login-pin"
                  disabled={loading || pin.length !== 6}
                  type="submit"
                  className="w-full h-12 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-medium"
                >
                  {loading ? "Verifying…" : "Log in"}
                </button>
                <button type="button" onClick={switchToOtp}
                        data-testid="me-forgot-pin"
                        className="w-full text-xs text-[#DC2626] underline inline-flex items-center justify-center gap-1">
                  <KeyReturn size={12}/>Forgot passcode? Log in with OTP
                </button>
                <button type="button" onClick={startPasscodeReset}
                        data-testid="me-reset-pin"
                        className="w-full text-[11px] text-[#8A8780] hover:text-[#DC2626] underline">
                  Or reset my passcode with a fresh code
                </button>
              </form>
            ) : (
              <form onSubmit={submitCode} className="space-y-4" data-testid="me-login-otp-form">
                <div className="text-sm text-[#5C5A55]">
                  Code sent to <span className="font-mono font-medium text-[#1A1A1A]">{phone}</span>
                  <button type="button" onClick={() => setStep(1)} className="ml-2 text-[#DC2626] text-xs underline">change</button>
                </div>
                {devOtp && (
                  <div className="p-3 rounded-md bg-[#F9E4B7] border border-[#E5C978] text-center">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A5A11] font-semibold">
                      Demo mode · your OTP is
                    </div>
                    <div className="mt-1 font-mono text-3xl tracking-[0.4em] text-[#7A5A11] font-bold">{devOtp}</div>
                  </div>
                )}
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                    Enter 6-digit OTP
                  </label>
                  <div className="relative">
                    <ShieldCheck size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]" />
                    <input
                      data-testid="me-otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="••••••"
                      autoFocus
                      inputMode="numeric"
                      maxLength={6}
                      className="w-full h-16 pl-10 pr-3 border-2 border-[#DC2626] rounded-md text-center text-3xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40"
                    />
                  </div>
                </div>
                <button
                  data-testid="me-verify-otp"
                  disabled={loading || otp.length !== 6}
                  type="submit"
                  className="w-full h-12 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-medium"
                >
                  {loading ? "Verifying…" : "Verify & continue"}
                </button>
                <button type="button" onClick={() => { setMode("pin"); setStep(2); }}
                        data-testid="me-back-to-pin"
                        className="w-full text-xs text-[#DC2626] underline">
                  I remember my passcode — go back
                </button>
              </form>
            )}
          </div>

          <div className="mt-6 text-center text-xs text-[#4B5563]">
            Don&apos;t have an account yet?{" "}
            <Link to="/register" className="text-[#DC2626] font-bold underline">Sign up free</Link>
          </div>

          {/* Iter 152 · Trust footer with 4 icons */}
          <div className="mt-5 bg-white rounded-2xl border border-gray-100 shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-3 grid grid-cols-4 gap-2">
            {[
              { l: "10k+",   s: "Customers"  },
              { l: "4.8★",   s: "Rated"      },
              { l: "24×7",   s: "Support"    },
              { l: "CPCB",   s: "Compliant"  },
            ].map(item => (
              <div key={item.l} className="text-center">
                <div className="text-[13px] font-black text-[#DC2626]">{item.l}</div>
                <div className="text-[9px] text-gray-500 mt-0.5 uppercase tracking-wider font-bold">{item.s}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-center gap-4 text-[10px] uppercase tracking-widest text-[#9CA3AF] font-bold">
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]"/>Trusted</span>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]"/>Secure</span>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#15803D]"/>Verified</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
